// Copyright 2020 Stanford University see LICENSE for license

import rdf from "rdf-ext"
import { nanoid } from "nanoid"
import _ from "lodash"
import { loadResourceTemplate } from "actionCreators/templates"
import { addSubject as addSubjectAction, setUnusedRDF } from "actions/resources"
import { selectProperty, selectSubject, selectValue } from "selectors/resources"
import {
  newLiteralValue,
  newUriValue,
  newValueSubject,
} from "utilities/valueFactory"
import { clearErrors, addError } from "actions/errors"
import { fetchResource } from "sinopiaApi"
import { findRootResourceTemplateId } from "utilities/Utilities"
import { chooseLang } from "utilities/Language"
import Config from "Config"

// Work-Instance relationship property URIs to exclude when copying resources
const skipPropertyUris = [
  "http://id.loc.gov/ontologies/bibframe/hasInstance",
  "http://id.loc.gov/ontologies/bibframe/instanceOf",
]

/**
 * Helper methods that should only be used in 'actionCreators/resources'
 */

/**
 * A thunk that loads an existing resource from Sinopia API and adds to state.
 * @return {[resource, unusedDataset]} if successful
 */
export const loadResource =
  (uri, errorKey, { asNewResource = false, version = null } = {}) =>
  (dispatch) => {
    dispatch(clearErrors(errorKey))
    return fetchResource(uri, { version })
      .then(([dataset, response]) => {
        if (!dataset) return false
        const resourceTemplateId = resourceTemplateIdFromDataset(uri, dataset)
        return dispatch(
          addResourceFromDataset(
            dataset,
            uri,
            resourceTemplateId,
            errorKey,
            asNewResource,
            _.pick(response, ["group", "editGroups"])
          )
        )
          .then(([resource, usedDataset]) => {
            const unusedDataset = dataset.difference(usedDataset)
            dispatch(
              setUnusedRDF(
                resource.key,
                unusedDataset.size > 0 ? unusedDataset.toCanonical() : null
              )
            )
            return [response, resource, unusedDataset]
          })
          .catch((err) => {
            // ResourceTemplateErrors have already been dispatched.
            if (err.name !== "ResourceTemplateError") {
              console.error(err)
              dispatch(
                addError(
                  errorKey,
                  `Error retrieving ${uri}: ${err.message || err}`
                )
              )
            }
            return false
          })
      })
      .catch((err) => {
        console.error(err)
        dispatch(
          addError(errorKey, `Error retrieving ${uri}: ${err.message || err}`)
        )
        return false
      })
  }

export const addResourceFromDataset =
  (
    dataset,
    uri,
    resourceTemplateId,
    errorKey,
    asNewResource,
    otherResourceAttrs = {}
  ) =>
  (dispatch) => {
    const subjectTerm = rdf.namedNode(chooseURI(dataset, uri))
    const newUri = asNewResource ? null : uri
    const context = {
      usedDataset: rdf.dataset(),
      dataset,
      errorKey,
      resourceTemplatePromises: {},
      // Terms already being expanded on the current path, so that data which
      // links back on itself does not recurse forever. Replaced with a copy
      // at each level rather than mutated, so it tracks the path rather than
      // everything ever seen.
      ancestors: new Set(),
    }
    context.usedDataset.addAll(
      context.dataset.match(
        subjectTerm,
        rdf.namedNode("http://sinopia.io/vocabulary/hasResourceTemplate")
      )
    )
    return dispatch(
      recursiveResourceFromDataset(
        subjectTerm,
        newUri,
        resourceTemplateId,
        false,
        context
      )
    ).then((resource) => {
      // Do not copy group or editGroups (passed in via otherResourceAttrs) if resource is new (i.e., copied)
      const newResource = _.merge(resource, otherResourceAttrs)
      if (asNewResource) {
        newResource.group = null
        newResource.editGroups = []
        // Strip Work-Instance relationship properties from copy
        newResource.properties = newResource.properties.filter(
          (property) =>
            !Object.keys(property.propertyTemplate?.uris || {}).some((uri) =>
              skipPropertyUris.includes(uri)
            )
        )
      }

      dispatch(addSubjectAction(newResource))
      return [newResource, context.usedDataset]
    })
  }

// The provided URI or <>.
export const chooseURI = (dataset, uri) =>
  dataset.match(rdf.namedNode(uri)).size > 0 ? uri : ""

export const addEmptyResource = (resourceTemplateId, errorKey) => (dispatch) =>
  dispatch(newSubject(null, resourceTemplateId, {}, errorKey)).then((subject) =>
    dispatch(newPropertiesFromTemplates(subject, false, errorKey)).then(
      (properties) => {
        const promises = properties.map((property) =>
          dispatch(expandProperty(property, errorKey))
        )
        return Promise.all(promises)
          .then((expandedProperties) => {
            subject.properties = expandedProperties
            return dispatch(addSubjectAction(subject))
          })
          .then(() => subject)
      }
    )
  )

const expandProperty = (property, errorKey) => (dispatch) => {
  if (property.propertyTemplate.type === "resource") {
    property.values = []
    const promises = property.propertyTemplate.valueSubjectTemplateKeys.map(
      (resourceTemplateId) =>
        dispatch(newSubject(null, resourceTemplateId, {}, errorKey)).then(
          (subject) =>
            dispatch(newPropertiesFromTemplates(subject, false, errorKey)).then(
              (properties) => {
                subject.properties = properties
                const newValue = newValueSubject(
                  property,
                  property.propertyTemplate.defaultUri,
                  subject
                )
                property.values.push(newValue)
                property.show = true
              }
            )
        )
    )
    return Promise.all(promises).then(() => property)
  }
  property.values = defaultValuesFor(property)
  property.show = true
  return property
}

export const recursiveResourceFromDataset =
  (subjectTerm, uri, resourceTemplateId, suppress, context) => (dispatch) => {
    // Everything below this subject is expanded with this subject recorded as
    // an ancestor. usedDataset and the template cache stay shared by
    // reference; only the ancestor set is per-branch.
    const childContext = {
      ...context,
      ancestors: new Set(context.ancestors).add(
        ancestorKeyFor(subjectTerm, resourceTemplateId)
      ),
    }
    return dispatch(
      newSubjectFromDataset(subjectTerm, uri, resourceTemplateId, context)
    ).then((subject) =>
      dispatch(
        newPropertiesFromTemplates(subject, true, context.errorKey)
      ).then((properties) =>
        Promise.all(
          properties.map((property) =>
            dispatch(
              newValuesFromDatasetByProperty(
                subjectTerm,
                property,
                suppress,
                childContext
              )
            ).then((values) => {
              const compactValues = _.compact(_.flatten(values))
              if (!_.isEmpty(compactValues)) {
                property.values = compactValues
                // If ordered, set property uri
                if (property.propertyTemplate.ordered)
                  property.propertyUri = _.first(compactValues).propertyUri
              }

              return compactValues
            })
          )
        ).then(() => {
          subject.properties = properties
          return subject
        })
      )
    )
  }

// Keyed on the template as well as the term: the same node can legitimately
// expand differently under two different templates, and including the
// template still bounds the traversal.
const ancestorKeyFor = (subjectTerm, resourceTemplateId) =>
  `${subjectTerm.value} ${resourceTemplateId}`

// A value subject that identifies the object without expanding any of its
// properties. Used where descending further would re-enter a node already
// being expanded on the current path.
const newReferenceSubjectFromObject =
  (obj, uri, resourceTemplateId, context) => (dispatch) =>
    dispatch(newSubjectFromDataset(obj, uri, resourceTemplateId, context)).then(
      (subject) =>
        dispatch(
          newPropertiesFromTemplates(subject, true, context.errorKey)
        ).then((properties) => {
          subject.properties = properties
          return subject
        })
    )

const newSubjectFromDataset =
  (subjectTerm, uri, resourceTemplateId, context) => (dispatch) =>
    dispatch(
      newSubject(
        uri,
        resourceTemplateId,
        context.resourceTemplatePromises,
        context.errorKey
      )
    ).then((subject) => {
      // Add classes
      const typeQuads = context.dataset
        .match(
          subjectTerm,
          rdf.namedNode("http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
        )
        .toArray()
      context.usedDataset.addAll(typeQuads)
      subject.classes = typeQuads.map((quad) => quad.object.value)
      return subject
    })

export const newSubject =
  (uri, resourceTemplateId, resourceTemplatePromises, errorKey) =>
  (dispatch) => {
    const key = nanoid()
    return dispatch(
      loadResourceTemplate(
        resourceTemplateId,
        resourceTemplatePromises,
        errorKey
      )
    ).then((subjectTemplate) => {
      // This handles if there was an error fetching resource template
      if (!subjectTemplate) {
        const err = new Error(`Unable to load ${resourceTemplateId}`)
        err.name = "ResourceTemplateError"
        console.error(err.toString())
        throw err
      }

      return {
        key,
        uri: _.isEmpty(uri) ? null : uri,
        subjectTemplate,
        properties: [],
      }
    })
  }

export const newPropertiesFromTemplates =
  (subject, noDefaults, errorKey) => (dispatch) =>
    Promise.all(
      subject.subjectTemplate.propertyTemplates.map((propertyTemplate) =>
        dispatch(newProperty(subject, propertyTemplate, noDefaults, errorKey))
      )
    )

const newValuesFromDatasetByProperty =
  (subjectTerm, property, suppress, context) => (dispatch) =>
    Promise.all(
      Object.keys(property.propertyTemplate.uris).flatMap((propertyUri) =>
        dispatch(
          newValuesFromDatasetByPropertyUri(
            subjectTerm,
            property,
            propertyUri,
            suppress,
            context
          )
        )
      )
    )

const newValuesFromDatasetByPropertyUri =
  (subjectTerm, property, propertyUri, suppress, context) => (dispatch) => {
    // Get the objects for the values. How depends on whether property is ordered or suppressed.
    let objects = null
    if (suppress) {
      objects = [subjectTerm]
    } else if (property.propertyTemplate.ordered) {
      objects = orderedObjects(subjectTerm, propertyUri, context)
    } else {
      objects = unorderedObjects(subjectTerm, propertyUri, context)
    }

    if (property.propertyTemplate.type === "resource") {
      // Get the values based on the template and the dataset then merge.
      // Deliberately not compacted: the results are zipped back against
      // objects below, so the two arrays must stay index-aligned.
      const objPromises = objects.map((obj) =>
        dispatch(
          newNestedResourceFromObject(obj, property, propertyUri, context)
        )
      )
      return Promise.all(objPromises).then((valuesFromObjs) => {
        // An object that produced no value leaves its triples unaccounted
        // for. unorderedObjects() marked the link quad used before that was
        // known, so put it back -- otherwise the dropped data is neither
        // shown in the editor nor reported as unused RDF, and disappears
        // silently on save.
        if (!suppress && !property.propertyTemplate.ordered) {
          objects.forEach((obj, index) => {
            if (valuesFromObjs[index]) return
            context.usedDataset.delete(
              rdf.quad(subjectTerm, rdf.namedNode(propertyUri), obj)
            )
          })
        }
        if (_.isEmpty(valuesFromObjs)) return []
        const templatePromises = templatePromisesFor(
          property,
          context.errorKey,
          dispatch
        )
        return Promise.all(templatePromises).then((valuesFromTemplates) =>
          mergeValues(valuesFromTemplates, valuesFromObjs)
        )
      })
    }
    return Promise.all(
      objects.map((obj) => {
        if (obj.termType === "NamedNode") {
          // URI
          return Promise.resolve(
            newUriFromObject(obj, property, propertyUri, context)
          )
        }
        if (obj.termType === "Literal") {
          return Promise.resolve(
            newLiteralFromObject(obj, property, propertyUri)
          )
        }
        // A blank node on a literal or uri property: only a resource property
        // can hold one. Its value is the label the parser invented for it, so
        // treating it as a literal would write a parser artifact into the
        // record. Drop the value instead and let the triples be reported as
        // unused RDF, which is what they are until a template can hold them.
        return Promise.resolve(null)
      })
    ).then((values) => {
      // Same reasoning as the resource branch above: unorderedObjects() marked
      // each link quad used before it was known whether the object would yield
      // a value, so release the ones that did not. Otherwise the dropped
      // triple is neither shown nor reported, and disappears on save.
      if (!suppress && !property.propertyTemplate.ordered) {
        objects.forEach((obj, index) => {
          if (values[index]) return
          context.usedDataset.delete(
            rdf.quad(subjectTerm, rdf.namedNode(propertyUri), obj)
          )
        })
      }
      return values
    })
  }

// Promises that return values based on template
const templatePromisesFor = (property, errorKey, dispatch) =>
  property.propertyTemplate.valueSubjectTemplateKeys.map((resourceTemplateId) =>
    dispatch(newSubject(null, resourceTemplateId, {}, errorKey)).then(
      (subject) =>
        // noDefaults is true: these subjects are placeholders for every
        // valueSubjectTemplateKeys entry, used by mergeValues to find the one
        // matching the dataset. Populating them with template defaults would let
        // those defaults leak into merged values as extra siblings, or entirely
        // replace real data whenever the dataset value isn't recognized (e.g., an
        // external authority URI with no local rdf:type triple to match against).
        dispatch(newPropertiesFromTemplates(subject, true, errorKey)).then(
          (properties) => {
            subject.properties = properties
            return newValueSubject(
              property,
              property.propertyTemplate.defaultUri,
              subject
            )
          }
        )
    )
  )

// Merge the values from the dataset and templates
const mergeValues = (valuesFromTemplates, valuesFromObjs) => {
  const valuesFromObjsMap = valuesMapFor(valuesFromObjs)
  const newValues = []
  valuesFromTemplates.forEach((valueFromTemplate) => {
    // Use values from dataset.
    // Otherwise, use values from template.
    const key = valuesMapKeyFor(valueFromTemplate)
    if (valuesFromObjsMap[key]) {
      valuesFromObjsMap[key].forEach((valueFromObj) =>
        newValues.push(valueFromObj)
      )
    } else {
      newValues.push(valueFromTemplate)
    }
  })
  return newValues
}

// Construct map of subject template key and property uri to value
const valuesMapFor = (values) => {
  const valuesMap = {}
  values.forEach((value) => {
    if (_.isEmpty(value)) return
    const key = valuesMapKeyFor(value)
    if (!valuesMap[key]) valuesMap[key] = []
    valuesMap[key].push(value)
  })
  return valuesMap
}

const valuesMapKeyFor = (value) =>
  [
    value.valueSubject.subjectTemplate.key,
    value.propertyKey || value.property.key,
  ].join("-")

const orderedObjects = (subjectTerm, propertyUri, context) => {
  // All quads for this property uri
  const quads = context.dataset
    .match(subjectTerm, rdf.namedNode(propertyUri))
    .toArray()
  // Should only be one.
  if (quads.length > 1) {
    console.error(`More than one quad for ordered property ${propertyUri}.`)
    return []
  }
  if (quads.length === 0) return []
  context.usedDataset.addAll(quads)
  const objects = []
  recursiveOrderedObjects(quads[0].object, objects, context)
  return objects
}

const recursiveOrderedObjects = (subjectTerm, objects, context) => {
  const firstQuad = context.dataset
    .match(
      subjectTerm,
      rdf.namedNode("http://www.w3.org/1999/02/22-rdf-syntax-ns#first")
    )
    .toArray()[0]
  if (!firstQuad) return
  context.usedDataset.add(firstQuad)
  objects.push(firstQuad.object)
  const restQuad = context.dataset
    .match(
      subjectTerm,
      rdf.namedNode("http://www.w3.org/1999/02/22-rdf-syntax-ns#rest")
    )
    .toArray()[0]
  context.usedDataset.add(restQuad)
  if (
    restQuad.object.value !== "http://www.w3.org/1999/02/22-rdf-syntax-ns#nil"
  )
    recursiveOrderedObjects(restQuad.object, objects, context)
}

const unorderedObjects = (subjectTerm, propertyUri, context) => {
  // All quads for this property uri
  const quads = context.dataset
    .match(subjectTerm, rdf.namedNode(propertyUri))
    .toArray()
  context.usedDataset.addAll(quads)
  return quads.map((quad) => quad.object)
}

const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"

// A property that captures nothing but the value's own rdf:type. That type is
// what matched the value to this template in the first place, so echoing it
// back is not data the template captured.
const isTypeOnlyProperty = (property) => {
  const uris = Object.keys(property.propertyTemplate?.uris || {})
  return !_.isEmpty(uris) && uris.every((uri) => uri === RDF_TYPE)
}

// True when the dataset actually supplied a value for any of the subject's
// properties. A subject with none renders as an empty nested form.
const subjectHasValues = (subject) =>
  subject.properties.some(
    (property) => !_.isEmpty(property.values) && !isTypeOnlyProperty(property)
  )

const newNestedResourceFromObject =
  (obj, property, propertyUri, context) => (dispatch) => {
    // Only build this embedded resource if can find the resource template.
    // Multiple types may be provided.
    const typeQuads = context.dataset
      .match(
        obj,
        rdf.namedNode("http://www.w3.org/1999/02/22-rdf-syntax-ns#type")
      )
      .toArray()

    // Among the valueTemplateRefs, find all of the resource templates that match a type.
    // Ideally, only want 1 but need to handle other cases.
    return Promise.all(
      typeQuads.map((typeQuad) =>
        dispatch(
          selectResourceTemplateId(
            property.propertyTemplate,
            typeQuad.object.value,
            context
          )
        )
      )
    ).then((childRtIds) => {
      const compactChildRtIds = _.uniq(_.compact(_.flatten(childRtIds)))

      return dispatch(
        resolveAmbiguousChildRtIds(compactChildRtIds, context)
      ).then((childRtId) => {
        if (childRtId) {
          context.usedDataset.addAll(typeQuads)

          // One resource template
          return dispatch(
            loadResourceTemplate(
              childRtId,
              context.resourceTemplatePromises,
              context.errorKey
            )
          ).then((subjectTemplate) => {
            // Suppression only makes sense when the matched template is
            // itself suppressible (a single URI/lookup property) -- a
            // NamedNode matching a template with more properties describes
            // itself via its own real triples, not a single substituted
            // value. When not suppressing, preserve the object's own URI as
            // the new subject's identity so it round-trips as itself rather
            // than a fresh blank node.
            const suppress =
              obj.termType === "NamedNode" && !!subjectTemplate?.suppressible
            const uri =
              !suppress && obj.termType === "NamedNode" ? obj.value : null

            // Stop before re-entering a node already being expanded higher up
            // the current path. Data legitimately links back on itself -- a
            // work pointing at its instance, which points back at the work --
            // and following that indefinitely exhausts the heap and kills the
            // browser tab with no error. A suppressed value cannot recurse at
            // all (its template has a single URI property), so it is exempt.
            if (
              !suppress &&
              context.ancestors?.has(ancestorKeyFor(obj, childRtId))
            ) {
              // A blank node has no stable identifier, so there is nothing to
              // reference and the looping link cannot be kept. Drop the value;
              // newValuesFromDatasetByPropertyUri releases the link quad so it
              // is reported as unused RDF rather than vanishing. The type
              // quads stay marked used -- the outer expansion of this same
              // node does emit them.
              if (obj.termType !== "NamedNode") return null
              // A named node keeps its URI, so the link still round-trips as a
              // bare reference.
              return dispatch(
                newReferenceSubjectFromObject(obj, uri, childRtId, context)
              ).then((subject) =>
                newValueSubject(property, propertyUri, subject)
              )
            }

            return dispatch(
              recursiveResourceFromDataset(
                obj,
                uri,
                childRtId,
                suppress,
                context
              )
            ).then((subject) => {
              if (suppress || subjectHasValues(subject))
                return newValueSubject(property, propertyUri, subject)

              // The object carries nothing beyond its own rdf:type -- a bare
              // reference that happens to state its class, which is common in
              // LC data. The matched non-suppressible template has nothing to
              // put in any of its fields, so it would render as an empty
              // nested form with the reference invisible to the cataloger.
              if (obj.termType !== "NamedNode") {
                // A blank node with only a type has nothing to preserve: no
                // URI to fall back on and no values to show. Drop the value
                // and release its type quads so they are reported as unused
                // RDF rather than silently discarded on save.
                typeQuads.forEach((typeQuad) =>
                  context.usedDataset.delete(typeQuad)
                )
                return null
              }
              // Prefer the suppressible sibling when the property offers one:
              // it shows the reference in a single lookup field, which is all
              // the data actually says.
              return dispatch(
                selectSuppressibleResourceTemplateId(
                  property.propertyTemplate,
                  context
                )
              ).then((suppressibleRtId) => {
                // Without a suppressible sibling, keep the subject as it is.
                // Its URI is its identity, so it still round-trips.
                if (!suppressibleRtId)
                  return newValueSubject(property, propertyUri, subject)
                return dispatch(
                  recursiveResourceFromDataset(
                    obj,
                    null,
                    suppressibleRtId,
                    true,
                    context
                  )
                ).then((suppressedSubject) =>
                  newValueSubject(property, propertyUri, suppressedSubject)
                )
              })
            })
          })
        }

        // No local rdf:type triple matched a candidate template -- e.g. the
        // object is a bare reference to an external, shared vocabulary term or
        // resource, which has no reason to restate its own type locally. If the
        // object is a plain URI and exactly one candidate template is marked
        // suppressible (i.e., designed to round-trip as a flat URI with no
        // local type assertion), use that template directly rather than
        // discarding real data.
        if (obj.termType !== "NamedNode") {
          return null
        }
        return dispatch(
          selectSuppressibleResourceTemplateId(
            property.propertyTemplate,
            context
          )
        ).then((suppressibleRtId) => {
          if (!suppressibleRtId) return null
          return dispatch(
            recursiveResourceFromDataset(
              obj,
              null,
              suppressibleRtId,
              true,
              context
            )
          ).then((subject) => newValueSubject(property, propertyUri, subject))
        })
      })
    })
  }

// When a value's local rdf:type matches more than one candidate template,
// prefer a non-suppressible match: a suppressible template exists only to
// catch values with no local type at all, so if a value's asserted type
// also happens to match one, a non-suppressible candidate is always the
// more correct choice. Only throw if more than one non-suppressible
// candidate matches -- that's a genuinely ambiguous profile.
const resolveAmbiguousChildRtIds =
  (compactChildRtIds, { resourceTemplatePromises, errorKey }) =>
  (dispatch) => {
    if (compactChildRtIds.length <= 1)
      return Promise.resolve(compactChildRtIds[0])

    return Promise.all(
      compactChildRtIds.map((resourceTemplateId) =>
        dispatch(
          loadResourceTemplate(
            resourceTemplateId,
            resourceTemplatePromises,
            errorKey
          )
        ).then((subjectTemplate) => ({
          resourceTemplateId,
          suppressible: subjectTemplate?.suppressible,
        }))
      )
    ).then((candidates) => {
      const nonSuppressible = candidates.filter(
        (candidate) => !candidate.suppressible
      )
      if (nonSuppressible.length === 1)
        return nonSuppressible[0].resourceTemplateId
      throw `More than one resource template matches: ${compactChildRtIds}`
    })
  }

const selectResourceTemplateId =
  (propertyTemplate, resourceURI, { resourceTemplatePromises, errorKey }) =>
  (dispatch) =>
    Promise.all(
      // The keys are resource template ids. They may or may not be in state
      propertyTemplate.valueSubjectTemplateKeys.map((resourceTemplateId) =>
        dispatch(
          loadResourceTemplate(
            resourceTemplateId,
            resourceTemplatePromises,
            errorKey
          )
        ).then((subjectTemplate) => {
          // Check if resourceURI matches either the required class or any optional class
          if (!subjectTemplate) return undefined
          const allClasses = Object.keys(subjectTemplate.classes || {})
          const matches = allClasses.includes(resourceURI)
          return matches ? resourceTemplateId : undefined
        })
      )
    )

// Used only when no candidate template's class matched a local rdf:type
// triple on the object. Safe to use only when exactly one candidate is
// suppressible -- with more than one, there's no way to know which the value
// represents, so the caller should decline (return undefined) rather than guess.
const selectSuppressibleResourceTemplateId =
  (propertyTemplate, { resourceTemplatePromises, errorKey }) =>
  (dispatch) =>
    Promise.all(
      propertyTemplate.valueSubjectTemplateKeys.map((resourceTemplateId) =>
        dispatch(
          loadResourceTemplate(
            resourceTemplateId,
            resourceTemplatePromises,
            errorKey
          )
        ).then((subjectTemplate) =>
          subjectTemplate?.suppressible ? resourceTemplateId : undefined
        )
      )
    ).then((resourceTemplateIds) => {
      const compactIds = _.compact(resourceTemplateIds)
      return compactIds.length === 1 ? compactIds[0] : undefined
    })

// Literals ingested from outside Blue Core (LC, for example) frequently carry
// no language tag, which left every value in the editor reading "No language
// specified" until a cataloger opened the language modal on each one. Fall back
// to the same default that newBlankLiteralValue/newBlankUriValue already give a
// value the cataloger creates by hand, so loaded and new values behave alike.
// See https://github.com/blue-core-lod/sinopia_editor/issues/191
//
// Read the default from Config rather than the root subject: the subject is not
// in the store yet while its values are being built. reducers/resources seeds
// subject.defaultLang from this same constant.
const defaultLangFor = (property) =>
  chooseLang(
    property.propertyTemplate.languageSuppressed,
    Config.defaultLanguageId
  )

// Only a plain string literal may carry a language tag. A typed literal
// (xsd:date, xsd:integer, ...) must not, so it keeps whatever it arrived with.
// TemplatesBuilder already suppresses language for templates that declare a
// validationDataType; this guards data whose literal type disagrees with its
// template.
const isTaggable = (term) =>
  term.datatype?.value === "http://www.w3.org/2001/XMLSchema#string"

const newLiteralFromObject = (obj, property, propertyUri) => {
  const lang =
    obj.language || (isTaggable(obj) ? defaultLangFor(property) : null)
  return newLiteralValue(property, propertyUri, obj.value, lang)
}

const newUriFromObject = (obj, property, propertyUri, context) => {
  const uri = obj.value
  const labelQuads = context.dataset
    .match(obj, rdf.namedNode("http://www.w3.org/2000/01/rdf-schema#label"))
    .toArray()
  // No fallback to the uri. A label invented purely for display used to be
  // written back on save as though a cataloger had supplied it. The uri stays
  // visible in the URI field above the label, so nothing is hidden by leaving
  // this unset.
  let label = null
  let lang = null
  if (labelQuads.length > 0) {
    // First that doesn't start with http or first
    const labelQuad =
      labelQuads.find(
        (labelQuad) => !labelQuad.object.value.startsWith("http")
      ) || labelQuads[0]
    label = labelQuad.object.value
    lang =
      labelQuad.object.language ||
      (isTaggable(labelQuad.object) ? defaultLangFor(property) : null)
    // Adding all to usedData, even though only using first. This is to avoid user confusion over extra triples, e.g., https://github.com/LD4P/sinopia_editor/issues/2634
    context.usedDataset.addAll(labelQuads)
  }
  return newUriValue(property, propertyUri, uri, label, lang)
}

const newProperty =
  (subject, propertyTemplate, noDefaults, errorKey) => (dispatch) => {
    const key = nanoid()
    const property = {
      key,
      subject,
      propertyTemplate,
      values: null,
      show: false,
      propertyUri: null,
    }
    if (!noDefaults && !_.isEmpty(property.propertyTemplate.defaults)) {
      property.values = defaultValuesFor(property)
      if (!_.isEmpty(property.values)) property.show = true
    }

    // If ordered, then set property uri
    if (propertyTemplate.ordered)
      property.propertyUri = propertyTemplate.defaultUri

    // If required and we do not already have some default values, then expand the property.
    if (propertyTemplate.required && !property.values) {
      property.show = true
      return dispatch(
        valuesForExpandedProperty(property, noDefaults, errorKey)
      ).then((values) => {
        property.values = values
        return property
      })
    }

    return property
  }

export function defaultValuesFor(property) {
  return property.propertyTemplate.defaults.map((defaultValue) => {
    if (property.propertyTemplate.type === "uri") {
      return newUriValue(
        property,
        property.propertyTemplate.defaultUri,
        defaultValue.uri,
        defaultValue.label,
        defaultValue.lang
      )
    }
    return newLiteralValue(
      property,
      property.propertyTemplate.defaultUri,
      defaultValue.literal,
      defaultValue.lang
    )
  })
}

const valuesForExpandedProperty =
  (property, noDefaults, errorKey) => (dispatch) => {
    if (property.propertyTemplate.type === "resource") {
      return Promise.all(
        property.propertyTemplate.valueSubjectTemplateKeys.map(
          (resourceTemplateId) =>
            dispatch(newSubject(null, resourceTemplateId, {}, errorKey)).then(
              (subject) =>
                dispatch(
                  newPropertiesFromTemplates(subject, noDefaults, errorKey)
                ).then((properties) => {
                  subject.properties = properties
                  return newValueSubject(
                    property,
                    property.propertyTemplate.defaultUri,
                    subject
                  )
                })
            )
        )
      )
    }
    return Promise.resolve([])
  }

export const newSubjectCopy = (subjectKey, value) => (dispatch, getState) => {
  const subject = selectSubject(getState(), subjectKey)
  const newSubject = _.pick(subject, ["subjectTemplate", "classes"])

  // Add to value
  if (value) value.valueSubject = newSubject

  newSubject.key = nanoid()
  newSubject.uri = null
  newSubject.properties = []

  // Add properties
  return Promise.all(
    subject.properties.map((property) =>
      dispatch(newPropertyCopy(property.key, newSubject))
    )
  ).then(() => newSubject)
}

const newPropertyCopy = (propertyKey, subject) => (dispatch, getState) => {
  const property = selectProperty(getState(), propertyKey)

  // Skip Work-Instance relationship properties
  const templateUris = Object.keys(property.propertyTemplate?.uris || {})
  if (templateUris.some((uri) => skipPropertyUris.includes(uri))) {
    return Promise.resolve(null)
  }

  const newProperty = _.pick(property, [
    "propertyTemplate",
    "propertyUri",
    "show",
  ])

  // Add to subject
  subject.properties.push(newProperty)

  newProperty.key = nanoid()
  newProperty.subject = subject
  newProperty.values = property.valueKeys === null ? null : []

  // Add values
  if (property.values) {
    return Promise.all(
      property.values.map((value) =>
        dispatch(newValueCopy(value.key, newProperty))
      )
    ).then(() => newProperty)
  }
  return newProperty
}

const newValueCopy = (valueKey, property) => (dispatch, getState) => {
  const value = selectValue(getState(), valueKey)
  const newValue = _.pick(value, [
    "literal",
    "lang",
    "uri",
    "label",
    "propertyUri",
    "component",
  ])

  // Add to property
  property.values.push(newValue)

  newValue.key = nanoid()
  newValue.propertyKey = property.key
  newValue.valueSubject = null

  return newValue
}

export const resourceTemplateIdFromDataset = (uri, dataset) => {
  const resourceTemplateId = findRootResourceTemplateId(uri, dataset)
  return resourceTemplateId || null
}
